'use client';

import React, {FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {List, Avatar, Input, Row, Col, Drawer, Spin, Space, Alert, Button} from 'antd';
import {
    useLLMOutput,
    type UseLLMOutputReturn,
    type BlockMatch,
    type LLMOutputComponent
    // @ts-ignore
} from '@llm-ui/react';
import {markdownLookBack} from '@llm-ui/markdown';
import {
    findCompleteCodeBlock,
    findPartialCodeBlock,
    codeBlockLookBack,
} from '@llm-ui/code';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {useSelector} from "react-redux";
import {selectUser} from "../../../../redux/features/userSlice";
import {useParams} from "react-router-dom";
import {useChatThread} from "./utils/useChatThread";
import {UserOutlined} from '@ant-design/icons';
import {assistantBg, assistantBorder, assistantColor, CustomRobotIcon, userBg, userBorder, userColor} from "./utils/CustomRobotIcon";


const MarkdownRenderer: LLMOutputComponent = ({blockMatch}: { blockMatch: BlockMatch }) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {blockMatch.output}
    </ReactMarkdown>
);

const CodeRenderer: LLMOutputComponent = ({blockMatch}: { blockMatch: BlockMatch }) => (
    <pre style={{background: "#f5f5f5", padding: 12, borderRadius: 4}}>
    <code>{blockMatch.output}</code>
  </pre>
);

interface MessageRendererProps {
    content: string;
    isAssistant: boolean;
    loading?: boolean;
}

const MessageRenderer: FC<MessageRendererProps> = ({content, isAssistant, loading = false}) => {
    const llmData: UseLLMOutputReturn = useLLMOutput({
        llmOutput: content,
        fallbackBlock: {
            component: MarkdownRenderer,
            lookBack: markdownLookBack()
        },
        blocks: [
            {
                component: CodeRenderer,
                findCompleteMatch: findCompleteCodeBlock(),
                findPartialMatch: findPartialCodeBlock(),
                lookBack: codeBlockLookBack()
            }
        ],
        isStreamFinished: !loading
    });

    const blockMatches: BlockMatch[] = llmData.blockMatches;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: isAssistant ? 'flex-start' : 'flex-end',
                marginBottom: 16,
                width: '100%'
            }}
        >
            <div
                style={{
                    maxWidth: '75%',
                    display: 'flex',
                    flexDirection: isAssistant ? 'row' : 'row-reverse',
                    alignItems: 'flex-start',
                    gap: 8
                }}
            >
                <Avatar
                    size="small"
                    style={{
                        flexShrink: 0,
                        backgroundColor: isAssistant ? assistantColor : userColor,
                        color: '#fff'
                    }}
                    icon={isAssistant ? <CustomRobotIcon/> : <UserOutlined/>}
                />

                <div
                    style={{
                        backgroundColor: isAssistant ? assistantBg : userBg,
                        padding: '8px 20px',
                        borderRadius: '8px',
                        borderTopLeftRadius: isAssistant ? '2px' : '8px',
                        borderTopRightRadius: isAssistant ? '8px' : '2px',
                        border: `1px solid ${isAssistant ? assistantBorder : userBorder}`,
                        wordBreak: 'break-word',
                        position: 'relative'
                    }}
                >
                    <div
                        style={{
                            fontSize: '11px',
                            color: '#8c8c8c',
                            marginBottom: 4,
                            fontWeight: 500
                        }}
                    >
                        {isAssistant ? 'ERIK' : 'Tu'}
                    </div>

                    <div>
                        {loading ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                color: '#8c8c8c',
                                fontSize: '14px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    gap: '2px'
                                }}>
                                    <div
                                        style={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: assistantColor,
                                            animation: 'typing 1.4s infinite ease-in-out both',
                                            animationDelay: '0s'
                                        }}
                                    />
                                </div>
                                <span>Sto pensando...</span>
                            </div>
                        ) : (
                            blockMatches.map((bm, i) => {
                                const Comp = bm.block.component as LLMOutputComponent;
                                return <Comp key={i} blockMatch={bm}/>;
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const drawerTitle = (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0'
    }}>
        <div>
            <div style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#262626',
                lineHeight: 1.2
            }}>
                ERIK
            </div>
            <div style={{
                fontSize: '12px',
                color: '#8c8c8c',
                lineHeight: 1
            }}>
                Assistente AI
            </div>
        </div>
    </div>
);

const ChatAssistant = ({isOpen, setIsOpen}: { isOpen: boolean; setIsOpen: (v: boolean) => void }) => {
    const user = useSelector(selectUser);
    const {id: negotiationId} = useParams<{ id: string }>();

    const [inputValue, setInputValue] = useState<string>("")
    const [partialMessage, setPartialMessage] = useState<string>("");
    const [isWaitingFirstMessage, setIsWaitingFirstMessage] = useState<boolean>(false)

    const {
        threadId,
        isInitializing,
        initError,
        thread,
        retry
    } = useChatThread({
        negotiationUuid: negotiationId ?? "",
        underwriterUuid: user.uuid,
        streamOptions:
            {
                messagesKey: "messages",
                onLangChainEvent: (chunk) => onLangChainEvent(chunk),
                onError: () => setPartialMessage(""),
                onUpdateEvent: () => setPartialMessage(() => ""),
            }
    });

    const onLangChainEvent = useCallback((chunk: any) => {
        if (chunk.event === "on_chat_model_stream") {
            const token = chunk.data.chunk.content
            setPartialMessage(prevState => prevState + token)
        }
    }, [])

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (isOpen && thread && thread.messages.length > 0) {
            scrollContainerRef.current?.scrollTo(0, scrollContainerRef.current.scrollHeight);
        }
    }, [isOpen, thread]);

    // filtra messaggi umani + AI “pulite”
   /* const isHumanOrAiNoToolCalls = useMemo(
        () => (msg: any) => msg.type === "human" || (msg.type === "ai" && (!Array.isArray(msg.tool_calls) || msg.tool_calls.length === 0)),
        []
    );*/
    const finalMessages = thread.messages

    const messagesToShow = useMemo(() => {
        const filteredMessages = finalMessages.filter(msg => !("lc_serializable" in msg));
        if (partialMessage) {
            return [
                ...filteredMessages,
                {
                    id: "__partial__",
                    type: "ai",
                    content: partialMessage,
                    tool_calls: [],
                },
            ];
        }
        return filteredMessages;
    }, [finalMessages, partialMessage]);

    useEffect(() => {
        const reInitChat = retry
        let currentInterval: NodeJS.Timer | undefined = undefined

        if (finalMessages.length === 0) {
            setIsWaitingFirstMessage(true)
            currentInterval = setInterval(reInitChat, 2000)
        } else {
            if (currentInterval !== undefined) {
                clearInterval(currentInterval)
                setIsWaitingFirstMessage(false)
            }
        }

        return () => {
            if (currentInterval !== undefined) {
                clearInterval(currentInterval)
                setIsWaitingFirstMessage(false)
            }
        }
    }, [finalMessages.length, retry]);

    return (
        <Drawer
            title={drawerTitle}
            placement="right"
            width={500}
            onClose={() => setIsOpen(false)}
            open={isOpen}
            destroyOnClose
            mask={false}
            bodyStyle={{overflowY: "hidden", padding: 0}}
            footer={
                <Input.Search
                    placeholder="Scrivi un messaggio…"
                    value={inputValue}
                    onChange={e => setInputValue(e.currentTarget.value)}
                    enterButton="Invia"
                    loading={thread.isLoading}
                    disabled={thread.isLoading || isInitializing || !threadId || isWaitingFirstMessage}
                    onSearch={(value: string) => {
                        thread.submit({messages: [{type: "human", content: value}]});
                        setInputValue("")
                    }}
                    style={{marginTop: 16}}
                />
            }
        >
            {(isInitializing || (threadId && isWaitingFirstMessage)) && (
                <div style={{textAlign: "center", padding: 20}}>
                    <Spin/> <span>ERIK si sta preparando…</span>
                </div>
            )}
            {initError && <Alert type="error" message={initError} style={{marginBottom: 16}}/>}
            {!isInitializing && !isWaitingFirstMessage && !initError && threadId && (
                <Row
                  align="bottom"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100%",
                    overflowY: "scroll",
                    scrollBehavior: "auto",
                    padding: 16
                  }}
                  ref={scrollContainerRef}
                >
                <Col span={24}>
                        <List
                            dataSource={messagesToShow}
                            loading={thread.isLoading && finalMessages.length === 0}
                            renderItem={(msg: any) => (
                                <MessageRenderer
                                    key={msg.id}
                                    content={msg.content}
                                    isAssistant={msg.type === "ai" || msg.type === "tool"}
                                />
                            )}
                        />
                    </Col>
                </Row>
            )}
            {(!isInitializing && !initError && !threadId &&
                <Row align="bottom" style={{height: "100%"}}>
                    <Space direction={"vertical"}>
                        <Alert showIcon type={"warning"} message={"Dati non processati per questa trattativa, impossibile inizializzare la chat."}/>
                    </Space>
                </Row>
            )}
        </Drawer>
    );
};

export default ChatAssistant;
